import { useLink, ChevronIconWrapper, CommonContextProvider } from "../ApacCandidateFooter/components/Accordion/Accordion.mjs";
import "./components/ImpersonationBanner/ImpersonationBanner.css.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
import { useMelwaysInfo, useMelwaysLink } from "@seek/melways-react";
import { Box, ContentBlock, useResponsiveValue, Divider, MenuRenderer, MenuItemDivider, MenuItem, MenuItemLink, Strong, IconTick, Badge, IconLanguage, Hidden, IconLocation } from "braid-design-system";
import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo, createContext, useContext } from "react";
import FocusLock from "react-focus-lock";
import throttle from "lodash/throttle.js";
import { AuthLinks } from "./components/AuthLinks/AuthLinks.mjs";
import { EmployersLink } from "./components/EmployersLink/EmployersLink.mjs";
import { ImpersonationBanner } from "./components/ImpersonationBanner/ImpersonationBanner.mjs";
import { AuthN, getConfig } from "@seek/online-identity";
import { HeaderLogo } from "./components/Logo/Logo.mjs";
import { useTranslations } from "@vocab/react";
import { translations } from "../../ApacCandidateHeader/components/MobileMenuToggle/.vocab/index.mjs";
import { ResponsiveDropdownMenu } from "./components/ResponsiveDropdownMenu/ResponsiveDropdownMenu.mjs";
import { Tabs } from "./components/Tabs/Tabs.mjs";
import { translations as translations$2 } from "../../.vocab/index.mjs";
import { HOMEPAGE_LANGUAGE_OVERRIDE_PATH, siteConfig } from "@seek/melways-sites";
import assert from "assert";
import { translations as translations$1 } from "../../ApacCandidateHeader/.vocab/index.mjs";
import { logoContainer, expandedLogoContainer, openMenu, headerZIndex } from "./ApacCandidateHeader.css.mjs";
var AUTHENTICATION_TYPES = /* @__PURE__ */ ((AUTHENTICATION_TYPES2) => {
  AUTHENTICATION_TYPES2["AUTHENTICATED"] = "authenticated";
  AUTHENTICATION_TYPES2["UNAUTHENTICATED"] = "unauthenticated";
  AUTHENTICATION_TYPES2["AUTH_PENDING"] = "pending";
  return AUTHENTICATION_TYPES2;
})(AUTHENTICATION_TYPES || {});
const PageBlock = ({
  children,
  width = "large",
  position,
  data
}) => /* @__PURE__ */ jsx(
  Box,
  {
    paddingX: {
      mobile: position === "header" ? "none" : "xsmall",
      tablet: "gutter"
    },
    data,
    children: /* @__PURE__ */ jsx(ContentBlock, { width, children })
  }
);
const brandDisplayNameMap = {
  jobstreet: "Jobstreet",
  jobsdb: "Jobsdb",
  seek: "SEEK"
};
const DEFAULT_COUNTRY = "au";
const useIsClientSideRendered = () => {
  const [isClientSideRendered, setIsClientSideRendered] = useState(false);
  useEffect(() => {
    setIsClientSideRendered(true);
  }, []);
  return isClientSideRendered;
};
const AGGRESSIVE_TAB_BUFFER = 10;
const useAggressiveTab = ({
  authenticationStatus,
  userProfile
}) => {
  const isClientSideRendered = useIsClientSideRendered();
  const responsiveValue = useResponsiveValue();
  const [isAggressiveTabs, setIsAggressiveTabs] = useState(false);
  const aggressiveTabsRef = useRef(null);
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const tabsRef = useRef(null);
  const isTablet = responsiveValue({ mobile: false, tablet: true, desktop: false }) || false;
  const isDesktop = responsiveValue({ mobile: false, desktop: true }) || false;
  const updateTriggerPoint = useCallback(() => {
    var _a, _b, _c, _d;
    const headerWrapperWidth = ((_a = headerRef == null ? void 0 : headerRef.current) == null ? void 0 : _a.getBoundingClientRect().width) ?? 0;
    const tabWidth = ((_b = tabsRef.current) == null ? void 0 : _b.getBoundingClientRect().width) ?? 0;
    const menuWidth = ((_c = menuRef == null ? void 0 : menuRef.current) == null ? void 0 : _c.getBoundingClientRect().width) ?? 0;
    const aggressiveTabWidth = ((_d = aggressiveTabsRef.current) == null ? void 0 : _d.getBoundingClientRect().width) ?? 0;
    const triggerPoint = menuWidth + AGGRESSIVE_TAB_BUFFER + tabWidth + aggressiveTabWidth;
    if (isTablet) {
      setIsAggressiveTabs(true);
    } else if (isDesktop) {
      setIsAggressiveTabs(headerWrapperWidth <= triggerPoint);
    }
  }, [isDesktop, isTablet]);
  useIsomorphicLayoutEffect(() => {
    updateTriggerPoint();
  }, []);
  useIsomorphicLayoutEffect(() => {
    updateTriggerPoint();
  }, [isClientSideRendered, authenticationStatus, userProfile]);
  useIsomorphicLayoutEffect(() => {
    const throttleUpdateTriggerPoint = throttle(
      () => {
        updateTriggerPoint();
      },
      1e3 / 60,
      {
        leading: true,
        trailing: true
      }
    );
    window.addEventListener("resize", throttleUpdateTriggerPoint);
    return () => {
      throttleUpdateTriggerPoint.cancel();
      window.removeEventListener("resize", throttleUpdateTriggerPoint);
    };
  }, [updateTriggerPoint]);
  useIsomorphicLayoutEffect(() => {
    if (isDesktop) {
      updateTriggerPoint();
      return;
    }
  }, [isDesktop]);
  return {
    ref: {
      aggressiveTabsRef,
      headerRef,
      menuRef,
      tabsRef
    },
    isAggressiveTabs
  };
};
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
const useCountries = () => {
  const urlResolver = useMelwaysLink();
  const countryUrl = useCallback(
    ({ country, language = "en" }) => urlResolver({
      language,
      country,
      path: HOMEPAGE_LANGUAGE_OVERRIDE_PATH,
      absolute: true
    }),
    [urlResolver]
  );
  return useMemo(
    () => ({
      sg: { link: countryUrl({ country: "sg" }), name: "Singapore" },
      my: { link: countryUrl({ country: "my" }), name: "Malaysia" },
      ph: { link: countryUrl({ country: "ph" }), name: "Philippines" },
      id: {
        link: countryUrl({
          country: "id",
          language: "id"
        }),
        name: "Indonesia"
      },
      hk: { link: countryUrl({ country: "hk" }), name: "Hong Kong" },
      th: {
        link: countryUrl({
          country: "th",
          language: "th"
        }),
        name: "Thailand"
      },
      au: { link: countryUrl({ country: "au" }), name: "Australia" },
      nz: { link: countryUrl({ country: "nz" }), name: "New Zealand" }
    }),
    [countryUrl]
  );
};
const useCountryList = () => {
  const countries = useCountries();
  const { country: melwaysCountry } = useMelwaysInfo();
  const country = melwaysCountry || DEFAULT_COUNTRY;
  return useMemo(() => {
    const countryList = Object.entries(countries).reduce(
      (all, [countryKey, { link, name }]) => {
        all.push({
          href: link,
          text: name,
          isActive: country === countryKey,
          objectType: "TabItem",
          linkContext: { source: `Country - ${name}` }
        });
        return all;
      },
      []
    );
    const sortedList = countryList.sort(
      (per, next) => per.text.localeCompare(next.text)
    );
    return {
      list: sortedList,
      activeCountryText: countries[country].name
    };
  }, [countries, country]);
};
const isDivider = (CustomComponent) => {
  const renderedCustomComponent = /* @__PURE__ */ jsx(CustomComponent, {});
  const result = typeof renderedCustomComponent === "object" && "type" in renderedCustomComponent && renderedCustomComponent.type === Divider;
  return result;
};
const DropdownMenu = ({
  icon: Icon,
  shouldApplyIconForActiveItem,
  items,
  label,
  onExpendCollapseLinkContextData,
  width
}) => {
  const { sendLinkEvent, injectLinkContextToData } = useLink();
  return /* @__PURE__ */ jsx(Box, { display: "flex", alignItems: "center", children: /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      align: "right",
      data: { automation: onExpendCollapseLinkContextData.source },
      offsetSpace: "small",
      width,
      onOpen: () => {
        sendLinkEvent({
          linkContext: {
            openingOrClosing: "opening",
            ...onExpendCollapseLinkContextData
          }
        });
      },
      onClose: ({ reason }) => {
        if (reason === "exit") {
          sendLinkEvent({
            linkContext: {
              openingOrClosing: "closing",
              ...onExpendCollapseLinkContextData
            }
          });
        }
      },
      trigger: ({ ...triggerProps }, { open }) => /* @__PURE__ */ jsx(Box, { userSelect: "none", cursor: "pointer", ...triggerProps, children: /* @__PURE__ */ jsx(
        ChevronIconWrapper,
        {
          icon: Icon,
          label,
          size: "small",
          upDirectionCondition: open
        }
      ) }),
      reserveIconSpace: shouldApplyIconForActiveItem,
      children: items.map((item, index) => {
        const key = `responsiveTab_${index}`;
        switch (item.objectType) {
          case "TabItem":
          case "TabItemLanguage":
            const tone = item.objectType === "TabItem" && item.tone === "critical" && "critical" || void 0;
            return /* @__PURE__ */ jsx(
              MenuItemLink,
              {
                tone,
                href: item.href,
                onClick: () => {
                  var _a;
                  (_a = item == null ? void 0 : item.handleClick) == null ? void 0 : _a.call(item);
                  sendLinkEvent({
                    linkContext: { ...item.linkContext, isExpanded: true }
                  });
                },
                data: injectLinkContextToData(item.linkContext, {
                  automation: item.linkContext.source
                }),
                badge: item.objectType === "TabItem" && item.isNew ? /* @__PURE__ */ jsx(Badge, { tone: "positive", children: "New" }) : void 0,
                icon: shouldApplyIconForActiveItem && item.isActive ? /* @__PURE__ */ jsx(IconTick, {}) : void 0,
                children: item.isActive ? /* @__PURE__ */ jsx(Strong, { children: item.text }) : item.text
              },
              key
            );
          case "TabItemWithCustomComponent":
            return isDivider(item.customComponent) ? /* @__PURE__ */ jsx(MenuItemDivider, {}, key) : /* @__PURE__ */ jsx(MenuItem, { children: /* @__PURE__ */ jsx(item.customComponent, {}) }, key);
        }
      })
    }
  ) });
};
const baseLabels = {
  en: "English",
  id: "Bahasa",
  th: "ภาษาไทย"
};
const useLanguageLabels = (enableZhForSSR) => {
  const labelsWithZh = useMemo(() => ({ ...baseLabels, zh: "中文" }), []);
  const [labels, setLabels] = useState(
    enableZhForSSR ? labelsWithZh : baseLabels
  );
  useEffect(() => {
    var _a;
    const allCookies = `; ${document.cookie}`;
    const parts = allCookies.split("; ENABLE_ZH_FOR_HEADER_FOOTER=");
    const enableZhForHFCookie = ((_a = parts.pop()) == null ? void 0 : _a.split(";").shift()) || void 0;
    const zhLanguageFlag = enableZhForSSR ?? enableZhForHFCookie;
    if (zhLanguageFlag) {
      setLabels(labelsWithZh);
    } else {
      setLabels(baseLabels);
    }
  }, [enableZhForSSR, labelsWithZh]);
  return labels;
};
const createLanguageSwitcherContent = (languageSwitcher, onLanguageSwitch, activeLanguage, labels) => ({
  activeLanguageText: labels[activeLanguage] || "",
  child: Object.entries(languageSwitcher).map(([languageRaw, node]) => {
    const language = languageRaw;
    return {
      objectType: "TabItem",
      href: node.url,
      linkContext: {
        source: `languageSwitcher - ${language}`,
        ...node.linkContext
      },
      text: labels[language] || "",
      isActive: activeLanguage === language,
      handleClick: () => {
        if (onLanguageSwitch) {
          onLanguageSwitch(language);
        }
      }
    };
  })
});
const LanguageSwitcher = ({
  languageSwitcherItem
}) => {
  if (!languageSwitcherItem) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    DropdownMenu,
    {
      icon: IconLanguage,
      label: languageSwitcherItem.text,
      onExpendCollapseLinkContextData: { source: "Language" },
      items: languageSwitcherItem.child,
      width: "small",
      shouldApplyIconForActiveItem: languageSwitcherItem.shouldApplyIconForActiveItem
    }
  );
};
const useMergeLanguageSwitcher = ({
  languageSwitcher,
  enableZhForSSR
}) => {
  const { site } = useMelwaysInfo();
  const urlResolver = useMelwaysLink();
  const labels = useLanguageLabels(enableZhForSSR);
  const supportedLanguages = Object.keys(labels);
  const { languages: siteLanguages } = siteConfig[site];
  const languages = siteLanguages.filter(
    (language) => supportedLanguages.includes(language)
  );
  return languages.length > 1 ? languages.reduce(
    (result, languageKey) => {
      result[languageKey] = (languageSwitcher == null ? void 0 : languageSwitcher[languageKey]) || {
        url: urlResolver({
          path: HOMEPAGE_LANGUAGE_OVERRIDE_PATH,
          language: languageKey
        })
      };
      return result;
    },
    {}
  ) : void 0;
};
const assertUrl = (url, {
  links,
  path,
  product,
  target,
  language
}) => {
  if (links) {
    assert(
      Boolean(url),
      `Link of "${target}" is not defined for language "${language}". This is an example of the link: ${JSON.stringify(
        links,
        null,
        2
      )}`
    );
  } else {
    assert(
      Boolean(url),
      `Link of "${path}" is not defined for product "${product}".`
    );
  }
  return url;
};
const isLocalisedLink = (link) => typeof link !== "string";
const isLinksRecord = (link) => typeof link !== "string" && typeof link !== "undefined";
const extractLink = (links, target, language, defaultLink) => {
  const link = links[target];
  if (link) {
    if (isLocalisedLink(link)) {
      return link[language];
    }
    return link;
  }
  return defaultLink;
};
const linkGenerator = ({ target, language }) => (baseLink, overrideLink) => {
  const getLink = () => {
    const baseLinksRecord = isLinksRecord(baseLink);
    const overrideLinksRecord = isLinksRecord(overrideLink);
    if (baseLinksRecord && overrideLinksRecord) {
      throw new Error(
        `Both baseLink and overrideLink should not be of type LinksRecord. Passed: ${JSON.stringify(
          baseLink
        )}, ${JSON.stringify(overrideLink)}`
      );
    }
    if (baseLinksRecord) {
      const foundBaseLink = extractLink(baseLink, target, language);
      if (foundBaseLink) {
        return foundBaseLink;
      }
    } else if (overrideLinksRecord) {
      const foundOverrideLink = extractLink(
        overrideLink,
        target,
        language,
        baseLink
      );
      if (foundOverrideLink) {
        return foundOverrideLink;
      }
    }
    return typeof baseLink === "string" ? baseLink : "";
  };
  const links = {
    ...isLinksRecord(baseLink) ? baseLink : {},
    ...overrideLink
  };
  const result = getLink();
  assertUrl(result, {
    target,
    language,
    links
  });
  return result;
};
const HOMEPAGE_REGEX = new RegExp("^/(en|id|th|)(?:/|$)$", "i");
const isHomepage = (path) => Boolean(path.match(HOMEPAGE_REGEX));
const useUrls = () => {
  const { brand, country: melwaysCountry, site, language } = useMelwaysInfo();
  const country = melwaysCountry || DEFAULT_COUNTRY;
  const countryDisplayCode = country.toUpperCase();
  const urlResolver = useMelwaysLink();
  return useMemo(() => {
    const forSite = linkGenerator({ target: site, language });
    const forCountry = linkGenerator({ target: country, language });
    const forBrand = linkGenerator({ target: brand, language });
    const link = (path) => {
      const isAbsolute = path.startsWith("http");
      const isHome = isHomepage(path);
      return isAbsolute ? path : urlResolver({
        path: isHome ? HOMEPAGE_LANGUAGE_OVERRIDE_PATH : path
      });
    };
    const employerLink = (path) => urlResolver({ path, product: "employer" });
    const urls = {
      about: {
        aboutUs: link(
          forBrand("/about", {
            seek: "/about"
          })
        ),
        investors: link(
          forBrand("https://www.seek.com.au/about/investors", {
            seek: "/about/investors"
          })
        ),
        newsroom: link("/about/news"),
        careers: link("/careers")
      },
      apps: {
        ios: forCountry({
          au: "https://itunes.apple.com/au/app/seek-jobs/id520400855?mt=8",
          nz: "https://itunes.apple.com/nz/app/seek-job-search/id520400855?mt=8",
          ph: "https://apps.apple.com/ph/app/jobstreet/id367294492",
          id: "https://apps.apple.com/id/app/jobstreet/id367294492",
          my: "https://apps.apple.com/my/app/jobstreet/id367294492",
          sg: "https://apps.apple.com/sg/app/jobstreet/id367294492",
          hk: "https://apps.apple.com/hk/app/jobsdb-job-search/id414607432",
          th: "https://apps.apple.com/th/app/jobsdb-job-search/id414607432"
        }),
        android: forCountry({
          au: "https://play.google.com/store/apps/details?id=au.com.seek&hl=en_AU",
          nz: "https://play.google.com/store/apps/details?id=au.com.seek&hl=en_AU",
          ph: "https://play.google.com/store/apps/details?id=com.jobstreet.jobstreet&hl=en",
          id: "https://play.google.com/store/apps/details?id=com.jobstreet.jobstreet&hl=id",
          my: "https://play.google.com/store/apps/details?id=com.jobstreet.jobstreet&hl=en",
          sg: "https://play.google.com/store/apps/details?id=com.jobstreet.jobstreet&hl=en",
          hk: "https://play.google.com/store/apps/details?id=com.jobsdb",
          th: "https://play.google.com/store/apps/details?id=com.jobsdb"
        })
      },
      careerAdvice: {
        exploreCareers: link("/career-advice/explore-careers"),
        exploreSalaries: link("/career-advice/explore-salaries"),
        root: link("/career-advice")
      },
      connect: {
        contactUs: link("/contact-us"),
        helpCenter: forSite({
          // https://myseek.atlassian.net/browse/DOC-14
          "candidate-seek-au": "https://help.seek.com.au/s/",
          "candidate-seek-nz": "https://help.seek.co.nz/s/",
          "candidate-jobsdb-th": {
            en: "https://help.th.jobsdb.com/th/s/?language=en_US",
            th: "https://help.th.jobsdb.com/th/s/?language=th"
          },
          "candidate-jobsdb-hk": "https://help.hk.jobsdb.com/s/",
          "candidate-jobstreet-id": {
            en: "https://help.id.jobstreet.com/id/s/?language=en_US",
            id: "https://help.id.jobstreet.com/id/s/?language=in"
          },
          "candidate-jobstreet-my": "https://help.my.jobstreet.com/s/",
          "candidate-jobstreet-ph": "https://help.ph.jobstreet.com/s/",
          "candidate-jobstreet-sg": "https://help.sg.jobstreet.com/s/"
        }),
        productAndTechBlog: "https://medium.com/seek-blog",
        videos: forCountry("https://video.seek.com.au", {
          nz: "https://video.seek.co.nz"
        })
      },
      employer: {
        customerService: employerLink("/contactus"),
        hiringAdvice: employerLink("/hiring-advice"),
        marketInsights: employerLink("/market-insights"),
        productsAndPrices: employerLink("/products/jobads"),
        recruitmentSoftwarePartners: employerLink(
          "/partners/connect-with-seek"
        ),
        root: employerLink("/")
      },
      legal: {
        privacy: link("/my-privacy"),
        securityPrivacy: link("/security-privacy"),
        tnc: link("/terms")
      },
      sites: {
        home: link("/"),
        businessForSale: `https://www.seekbusiness.com.au/?site=${country}&tracking=sk:main:${country}:nav:bus`,
        companies: link("/companies"),
        jobs: link("/"),
        volunteering: link(
          forCountry(
            `https://www.volunteer.com.au/?tracking=SKM${countryDisplayCode}:main+header`,
            {
              nz: `https://seekvolunteer.co.nz/?tracking=SKM${countryDisplayCode}:main+header`
            }
          )
        ),
        community: link("/community"),
        recruiters: link("/recruiters")
      },
      social: {
        facebook: forCountry({
          au: "https://www.facebook.com/SEEK/",
          nz: "https://www.facebook.com/seeknz/",
          ph: "https://www.facebook.com/JobstreetPH/",
          id: "https://www.facebook.com/JobStreetIndonesia",
          my: "https://www.facebook.com/jobstreetmalaysia/",
          sg: "https://www.facebook.com/jobstreetsingapore/",
          hk: "https://www.facebook.com/Jobsdb.HongKong",
          th: "https://www.facebook.com/JobsdbThailandOfficialPage/"
        }),
        instagram: forCountry({
          au: "https://www.instagram.com/seekau/",
          nz: "https://www.instagram.com/seeknz/",
          ph: "https://www.instagram.com/jobstreetph/",
          id: "https://www.instagram.com/jobstreetindonesia/",
          my: "https://www.instagram.com/jobstreet_malaysia/",
          sg: "https://www.instagram.com/sgjobstreet/",
          hk: "https://www.instagram.com/jobsdb_hk/",
          th: "https://www.instagram.com/jobsdbthailand/"
        }),
        twitter: forCountry({
          au: "https://twitter.com/seekjobs",
          nz: "https://twitter.com/seekjobsnz",
          ph: "https://twitter.com/JobStreetPH",
          id: "https://twitter.com/jobstreetcom",
          my: "https://twitter.com/JobStreetMY",
          sg: "https://twitter.com/JobStreetSG",
          hk: "https://twitter.com/HKJobs",
          th: "https://twitter.com/JobsdbTH"
        }),
        youtube: forCountry({
          au: "https://www.youtube.com/@SEEKJobs",
          nz: "https://www.youtube.com/@SEEKJobs",
          ph: "https://www.youtube.com/@JobStreetPhilippines",
          id: "https://www.youtube.com/@jobstreetbyseek",
          my: "https://www.youtube.com/@JobStreetMalaysiaYT",
          sg: "https://www.youtube.com/c/jobstreetsingaporeyt",
          hk: "https://www.youtube.com/@jobsdbhongkong2658",
          th: "https://www.youtube.com/@jobsdbthai"
        })
      },
      user: {
        appliedJobs: link("/my-activity/applied-jobs"),
        login: link("/oauth/login"),
        logout: link("/oauth/logout"),
        profile: {
          authenticated: link("/profile/me"),
          unauthenticated: link(
            forBrand("/profile", {
              seek: "/profile"
            })
          )
        },
        recommendedJobs: link("/recommended"),
        savedJobs: link("/my-activity/saved-jobs"),
        savedSearches: link("/my-activity/saved-searches"),
        settings: link("/settings"),
        signup: link("/oauth/register")
      }
    };
    return urls;
  }, [language, site, country, countryDisplayCode, urlResolver, brand]);
};
const appendReturnUrl = (url, returnUrl) => {
  const urlSuffix = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : "";
  return `${url}${urlSuffix}`;
};
const generateAuthenticatedUrl = ({
  isAuthenticated,
  loginUrl
}) => (url) => !isAuthenticated ? appendReturnUrl(loginUrl, url) : url;
const getUserProfileLink = ({
  isAuthenticated,
  profileLinks: { authenticated, unauthenticated }
}) => isAuthenticated ? authenticated : unauthenticated;
const getAuthN = () => new AuthN(getConfig("seekAnz-candidate", window.location), true);
const getContextFromIdToken = async () => {
  if (window.location.protocol === "http:") {
    return Promise.resolve(null);
  }
  try {
    const client = getAuthN();
    const identity = await client.getIdentity();
    if (!identity || !identity.act) {
      return null;
    }
    const user = await client.getUser();
    const showCustomerServiceView = user["http://seek/claims/customer_service_view"] === true;
    return {
      actor: {
        email: identity.act.email
      },
      subject: {
        email: identity.sub.email
      },
      showCustomerServiceView
    };
  } catch {
    return null;
  }
};
const Container = ({
  context: initialContext = null,
  additionalInfo,
  useAuthN = true
}) => {
  const [context, setContext] = useState(
    initialContext
  );
  useEffect(() => {
    if (useAuthN && context === null) {
      getContextFromIdToken().then(setContext);
    }
  }, [context, useAuthN]);
  if (!context) {
    return null;
  }
  return /* @__PURE__ */ jsx(ImpersonationBanner, { context, additionalInfo });
};
const initialState = {
  isMenuOpen: false,
  setIsMenuOpen: (_) => {
  }
};
const ApacCandidateHeaderContext = createContext(initialState);
const ApacCandidateHeaderProvider = ApacCandidateHeaderContext.Provider;
const useApacCandidateHeaderContext = () => {
  const { isMenuOpen, setIsMenuOpen } = useContext(ApacCandidateHeaderContext);
  return {
    isMenuOpen,
    setIsMenuOpen
  };
};
const linkContextSource = "Mobile Menu";
const MobileMenuToggle = () => {
  const { sendLinkEvent } = useLink();
  const { isMenuOpen, setIsMenuOpen } = useApacCandidateHeaderContext();
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(Hidden, { above: "mobile", children: /* @__PURE__ */ jsx(
    Box,
    {
      component: "button",
      cursor: "pointer",
      onClick: () => {
        setIsMenuOpen(!isMenuOpen);
        sendLinkEvent({
          linkContext: {
            source: linkContextSource,
            openingOrClosing: isMenuOpen ? "closing" : "opening"
          }
        });
      },
      "data-automation": linkContextSource,
      "aria-expanded": isMenuOpen,
      children: /* @__PURE__ */ jsx(
        ChevronIconWrapper,
        {
          label: t("Menu"),
          upDirectionCondition: isMenuOpen
        }
      )
    }
  ) });
};
const auOrgSchema = {
  socialLinks: [
    "https://www.instagram.com/seekau/",
    "https://twitter.com/seekjobs",
    "https://plus.google.com/+seekau",
    "https://au.linkedin.com/company/seek"
  ],
  telephone: "+61-1300-658-700",
  url: "https://www.seek.com.au"
};
const nzOrgSchema = {
  socialLinks: [
    "https://www.instagram.com/seeknz/",
    "https://twitter.com/seekjobsnz",
    "https://plus.google.com/+seeknz",
    "https://nz.linkedin.com/company/seek"
  ],
  telephone: "+64-0508-733-569",
  url: "https://www.seek.co.nz/"
};
const commonSocialLinks = [
  "https://www.facebook.com/SEEK/",
  "https://en.wikipedia.org/wiki/Seek_Limited",
  "https://www.youtube.com/user/SEEKJobs"
];
const generateStructureDataSchema = (locale = "au") => {
  if (!["au", "nz"].includes(locale)) {
    return void 0;
  }
  const { telephone, socialLinks, url } = locale === "nz" ? nzOrgSchema : auOrgSchema;
  return {
    "@context": "http://schema.org",
    "@type": "Organization",
    url,
    legalName: "Seek Limited",
    logo: "https://www.seek.com.au/content/images/logos/seek-logo-positive.svg",
    sameAs: [...commonSocialLinks, ...socialLinks],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone,
        contactType: "customer service",
        contactOption: "TollFree"
      }
    ]
  };
};
const StructuredDataSchema = ({ country }) => {
  const structureDataSchema = generateStructureDataSchema(country);
  return structureDataSchema ? /* @__PURE__ */ jsx(
    "script",
    {
      type: "application/ld+json",
      dangerouslySetInnerHTML: {
        __html: JSON.stringify(structureDataSchema)
      }
    }
  ) : null;
};
const isTabItem = (item) => (item == null ? void 0 : item.objectType) === "TabItem";
const updateIsActiveFlag = (items, activeTab) => activeTab ? items.map((item) => {
  if (isTabItem(item)) {
    return {
      ...item,
      isActive: item.tabId ? item.tabId === activeTab : false
    };
  }
  return item;
}) : items;
const parseUserName = (userProfile) => {
  var _a;
  return (userProfile == null ? void 0 : userProfile.firstName) || ((_a = userProfile == null ? void 0 : userProfile.email) == null ? void 0 : _a.split("@")[0]) || "";
};
const useLocalisation = ({
  activeAuthedUserTab,
  activePrimaryTab,
  authenticationStatus,
  languageSwitcher,
  leanHeader,
  onLanguageSwitch,
  returnUrlForPostAuth,
  userProfile,
  enableZhForSSR
}) => {
  const { brand, language, country: melwaysCountry } = useMelwaysInfo();
  const brandDisplayName = brandDisplayNameMap[brand];
  const { t } = useTranslations(translations$1);
  const { t: commonT } = useTranslations(translations$2);
  const labels = useLanguageLabels(enableZhForSSR);
  const country = melwaysCountry || DEFAULT_COUNTRY;
  const isAuthenticated = authenticationStatus === AUTHENTICATION_TYPES.AUTHENTICATED;
  const urls = useUrls();
  const search = {
    tabId: "search",
    href: urls.sites.jobs,
    text: commonT("Job search"),
    objectType: "TabItem",
    linkContext: { source: "job search" }
  };
  const profile = {
    tabId: "profile",
    href: getUserProfileLink({
      isAuthenticated,
      profileLinks: urls.user.profile
    }),
    text: commonT("Profile"),
    objectType: "TabItem",
    linkContext: { source: "profile" }
  };
  const careerAdvice = {
    tabId: "careerAdvice",
    href: urls.careerAdvice.root,
    text: commonT("Career advice"),
    objectType: "TabItem",
    linkContext: { source: "career advice" }
  };
  const community = {
    tabId: "community",
    isNew: true,
    href: urls.sites.community,
    text: commonT("Community"),
    objectType: "TabItem",
    linkContext: { source: "community" }
  };
  const company = {
    tabId: "company",
    href: urls.sites.companies,
    text: commonT("Explore companies"),
    objectType: "TabItem",
    linkContext: { source: "company reviews" }
  };
  const recruiter = {
    tabId: "recruiter",
    href: urls.sites.recruiters,
    text: commonT("Explore recruiters"),
    objectType: "TabItem",
    isNew: true,
    linkContext: { source: "recruiter directory" }
  };
  const login = {
    href: appendReturnUrl(urls.user.login, returnUrlForPostAuth),
    objectType: "TabItem",
    text: t("Sign in"),
    linkContext: { source: "sign in" }
  };
  const logout = {
    href: appendReturnUrl(urls.user.logout, returnUrlForPostAuth),
    text: t("Sign out"),
    objectType: "TabItem",
    tone: "critical",
    linkContext: { source: "sign out" }
  };
  const savedSearches = {
    tabId: "savedSearches",
    href: urls.user.savedSearches,
    objectType: "TabItem",
    text: commonT("Saved searches"),
    linkContext: { source: "saved searches" }
  };
  const savedJobs = {
    tabId: "savedJobs",
    href: urls.user.savedJobs,
    objectType: "TabItem",
    text: commonT("Saved jobs"),
    linkContext: { source: "saved jobs" }
  };
  const appliedJobs = {
    tabId: "appliedJobs",
    href: urls.user.appliedJobs,
    objectType: "TabItem",
    text: commonT("Applied jobs"),
    linkContext: { source: "applied jobs | application history" }
  };
  const recommendedJobs = {
    tabId: "recommendedJobs",
    href: urls.user.recommendedJobs,
    objectType: "TabItem",
    text: commonT("Recommended jobs"),
    linkContext: { source: "recommended jobs" }
  };
  const settings = {
    tabId: "settings",
    href: urls.user.settings,
    objectType: "TabItem",
    text: t("Settings"),
    linkContext: { source: "settings" }
  };
  const employersLogin = {
    href: urls.employer.root,
    text: commonT("Employer site"),
    objectType: "TabItem",
    linkContext: { source: "employer site | for employers" },
    tone: "formAccent",
    weight: "medium"
  };
  const divider = {
    customComponent: Divider,
    objectType: "TabItemWithCustomComponent"
  };
  const primaryTabsRaw = [
    search,
    profile,
    careerAdvice,
    company,
    ...brand === "seek" && (country === "au" || country === "nz") ? [recruiter] : [],
    ...brand === "jobstreet" ? [community] : []
  ];
  const primaryTabs = updateIsActiveFlag(primaryTabsRaw, activePrimaryTab);
  let authLinks;
  if (authenticationStatus === AUTHENTICATION_TYPES.UNAUTHENTICATED) {
    authLinks = {
      child: [login],
      objectType: "AuthNonMemberItem",
      linkContext: { source: "sign in" }
    };
  }
  const userName = parseUserName(userProfile);
  if (authenticationStatus === AUTHENTICATION_TYPES.AUTHENTICATED && userName) {
    const child = [
      { ...profile, tabId: "authedUserTabProfile" },
      savedSearches,
      savedJobs,
      appliedJobs,
      recommendedJobs,
      settings,
      divider,
      logout
    ];
    authLinks = {
      text: userName,
      child: updateIsActiveFlag(child, activeAuthedUserTab),
      objectType: "TabItemWithChild",
      linkContext: { source: "account name" },
      isExpanded: true
    };
  }
  const mappedLanguageSwitcher = useMergeLanguageSwitcher({
    languageSwitcher,
    enableZhForSSR
  });
  const languageSwitcherContent = mappedLanguageSwitcher ? createLanguageSwitcherContent(
    mappedLanguageSwitcher,
    onLanguageSwitch,
    language,
    labels
  ) : void 0;
  const languageSwitcherItem = languageSwitcherContent ? {
    text: languageSwitcherContent.activeLanguageText,
    child: languageSwitcherContent.child,
    objectType: "TabItemWithChild",
    linkContext: { source: "language" },
    Icon: IconLanguage,
    shouldApplyIconForActiveItem: true
  } : null;
  const { activeCountryText, list: countryList } = useCountryList();
  const languagesAndCountries = [
    {
      text: activeCountryText,
      child: countryList,
      objectType: "TabItemWithChild",
      linkContext: { source: "country" },
      Icon: IconLocation,
      shouldApplyIconForActiveItem: true
    },
    ...languageSwitcherItem ? [divider, languageSwitcherItem] : []
  ];
  return {
    logo: {
      href: urls.sites.home,
      linkContext: { source: "company logo" },
      seoText: brandDisplayName
    },
    primaryTabs: !leanHeader ? primaryTabs : void 0,
    responsiveNav: {
      navItem: {
        primary: [
          ...authLinks ? [divider, authLinks] : [],
          divider,
          ...languagesAndCountries,
          ...!leanHeader ? [divider, employersLogin] : []
        ]
      }
    },
    authLinks,
    employersLogin: !leanHeader ? employersLogin : void 0,
    languageSwitcherItem
  };
};
const ApacCandidateHeaderView = ({
  activeAuthedUserTab,
  activePrimaryTab,
  authenticationStatus,
  data,
  divider: headerDivider = false,
  languageSwitcher,
  leanHeader,
  onLanguageSwitch,
  returnUrlForPostAuth,
  userProfile,
  widthMode = "large",
  enableZhForSSR
}) => {
  const {
    authLinks,
    employersLogin,
    languageSwitcherItem,
    logo,
    primaryTabs,
    responsiveNav: mobileNav
  } = useLocalisation({
    activeAuthedUserTab,
    activePrimaryTab,
    authenticationStatus,
    languageSwitcher,
    leanHeader,
    onLanguageSwitch,
    returnUrlForPostAuth,
    userProfile,
    enableZhForSSR
  });
  const { brand, country: melwaysCountry } = useMelwaysInfo();
  const country = melwaysCountry || DEFAULT_COUNTRY;
  const {
    ref: { aggressiveTabsRef, headerRef, menuRef, tabsRef },
    isAggressiveTabs
  } = useAggressiveTab({ authenticationStatus, userProfile });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return /* @__PURE__ */ jsx(ApacCandidateHeaderProvider, { value: { isMenuOpen, setIsMenuOpen }, children: /* @__PURE__ */ jsxs(FocusLock, { disabled: !isMenuOpen, children: [
    brand === "seek" && /* @__PURE__ */ jsx(Container, {}),
    /* @__PURE__ */ jsxs(Box, { component: "header", data, children: [
      /* @__PURE__ */ jsx(StructuredDataSchema, { country }),
      /* @__PURE__ */ jsx(
        Box,
        {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: "full",
          background: "surface",
          position: {
            mobile: isMenuOpen ? "fixed" : void 0,
            tablet: "relative"
          },
          top: isMenuOpen ? 0 : void 0,
          left: isMenuOpen ? 0 : void 0,
          className: {
            [headerZIndex]: isMenuOpen
          },
          paddingY: ["none", "xsmall"],
          children: /* @__PURE__ */ jsxs(PageBlock, { width: widthMode, position: "header", children: [
            /* @__PURE__ */ jsxs(
              Box,
              {
                className: { [openMenu]: isMenuOpen },
                display: "flex",
                flexDirection: ["column", "row"],
                justifyContent: ["flexStart", "spaceBetween"],
                ref: headerRef,
                children: [
                  /* @__PURE__ */ jsxs(
                    Box,
                    {
                      ref: tabsRef,
                      display: "flex",
                      flexDirection: ["column", "row"],
                      children: [
                        /* @__PURE__ */ jsxs(
                          Box,
                          {
                            display: "flex",
                            className: [
                              logoContainer,
                              { [expandedLogoContainer]: isMenuOpen }
                            ],
                            alignItems: "center",
                            background: "surface",
                            justifyContent: ["spaceBetween", "flexStart"],
                            paddingX: ["xsmall", "none"],
                            paddingY: ["small", "none"],
                            marginRight: { tablet: "large" },
                            children: [
                              /* @__PURE__ */ jsx(HeaderLogo, { ...logo }),
                              /* @__PURE__ */ jsx(MobileMenuToggle, {})
                            ]
                          }
                        ),
                        primaryTabs && /* @__PURE__ */ jsx(
                          Box,
                          {
                            display: {
                              mobile: isMenuOpen ? "flex" : "none",
                              desktop: isAggressiveTabs ? "none" : "flex"
                            },
                            children: /* @__PURE__ */ jsx(Tabs, { tabList: primaryTabs })
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          ResponsiveDropdownMenu,
                          {
                            navItem: mobileNav.navItem,
                            isMenuOpen
                          }
                        )
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs(Box, { ref: menuRef, display: { mobile: "none", tablet: "flex" }, children: [
                    /* @__PURE__ */ jsx(
                      LanguageSwitcher,
                      {
                        languageSwitcherItem
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Box,
                      {
                        marginLeft: authenticationStatus !== AUTHENTICATION_TYPES.AUTH_PENDING ? "medium" : void 0,
                        "data-automation": "desktop-auth-links-wrapper",
                        children: /* @__PURE__ */ jsx(AuthLinks, { authItems: authLinks })
                      }
                    ),
                    employersLogin && /* @__PURE__ */ jsx(Box, { marginLeft: "medium", children: /* @__PURE__ */ jsx(EmployersLink, { ...employersLogin }) })
                  ] })
                ]
              }
            ),
            !leanHeader && primaryTabs && /* @__PURE__ */ jsx(
              Box,
              {
                display: {
                  mobile: "none",
                  tablet: "block",
                  desktop: isAggressiveTabs ? "flex" : "none"
                },
                paddingTop: "xxsmall",
                children: /* @__PURE__ */ jsx(Tabs, { tabList: primaryTabs, ref: aggressiveTabsRef })
              }
            )
          ] })
        }
      )
    ] }),
    headerDivider && /* @__PURE__ */ jsx(Divider, {})
  ] }) });
};
const ApacCandidateHeader = (props) => /* @__PURE__ */ jsx(CommonContextProvider, { position: "header", children: /* @__PURE__ */ jsx(ApacCandidateHeaderView, { ...props }) });
export {
  AUTHENTICATION_TYPES,
  ApacCandidateHeader,
  DEFAULT_COUNTRY,
  DropdownMenu,
  PageBlock,
  brandDisplayNameMap,
  generateAuthenticatedUrl,
  getUserProfileLink,
  isDivider,
  useApacCandidateHeaderContext,
  useCountries,
  useCountryList,
  useUrls
};
